/**
 * The hidden-holiday VISIBILITY RULE (THE-746 Phase 3, PR 3.3 / THE-764).
 *
 * ONE place answers "may this holiday be on screen?", because two surfaces ask
 * it — the calendar rail (`CalendarPage` → `holidayToCalendarEvent`) and the
 * Coming Up strip (`comingUpEntries.holidayEntriesForComingUp`) — and the
 * THE-746 ground rule is only as strong as its weakest asker. A filter written
 * twice is a filter that disagrees with itself once.
 *
 * Firebase-free and render-free on purpose, same split as
 * `calendar/holidayEvents.ts` and `components/comingUpEntries.ts`: the surfaces
 * that consume it pull Firebase at module top, so the decision lives where it
 * can be unit-tested without a DOM.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE DIRECTION IS LOAD-BEARING IN BOTH DIRECTIONS, so it is spelled out once:
 *
 *   `hiddenHolidays` is a HIDDEN list, never a shown list. There is
 *   deliberately no "show all" sentinel to misread.
 *
 *   - ABSENT field  → every holiday on.  This is every user until she opens
 *                     the Holidays page, i.e. nearly all of them. Read it as
 *                     all-OFF and the feature ships dark for everybody.
 *   - EMPTY array   → every holiday on.  Same state, reached by un-hiding
 *                     everything.
 *   - NOT ready yet → NOTHING on.        See below; this is the case the two
 *                     above make easy to get wrong.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY `ready` IS A SEPARATE INPUT AND NOT INFERRABLE FROM THE LIST.
 *
 * `useHolidayPrefs()` starts at `hiddenHolidays: []` and only sets `ready`
 * after the first `onSnapshot`. `[]` therefore means two opposite things at
 * two different moments: "she has hidden nothing" (after the snapshot) and "we
 * have not asked yet" (before it). Rendering the first reading during the
 * second window shows, for one or more frames, exactly the holidays she went
 * to the Holidays page to hide — a flash of someone else's religion on her
 * calendar. The list alone cannot distinguish the two; `ready` can, so it is
 * required rather than optional at every call site that matters.
 *
 * Signed-out is NOT the unready case: the hook sets `ready` immediately with
 * an empty list for a signed-out or anonymous visitor, which is correct — she
 * has no saved choices, so every holiday is on.
 *
 * The cost of failing closed is a brief holiday-less rail on a slow snapshot.
 * The cost of failing open is the ground-rule breach. Not a close call.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THE INPUT IS TYPED `unknown`.
 *
 * `theaWebUser.hiddenHolidays` is declared `string[] | undefined` and the
 * `updateHolidayPrefs` callable validates what it writes, so a wrong shape is
 * latent rather than live. But this module is what turns the field into
 * rendered output for the first time, and the shape arrives from a Firestore
 * document — not from the type system. `new Set(hiddenHolidays ?? [])`, which
 * is what the merge module did before this PR, survives `undefined` and `null`
 * and then throws inside a render on the one shape a hand-edited or
 * legacy-written document is most likely to carry: a map, `{christmas: true}`.
 * `??` does not catch a non-nullish object. So the field is read defensively
 * here, once, rather than trusted at two call sites.
 */

/** How a raw `theaWebUser.hiddenHolidays` value read. */
export type HiddenHolidaysRead =
  /** The field is not there (or is explicitly null) — the pre-Holidays-page
   *  user. Every holiday is on. */
  | { kind: 'absent'; hidden: readonly string[] }
  /** An array. `hidden` carries its usable members; junk members (non-strings,
   *  blank strings) are dropped rather than disqualifying the whole list,
   *  because the rest of it is still her real, saved choices. */
  | { kind: 'list'; hidden: readonly string[]; droppedMembers: boolean }
  /** Present but not an array — a map, a string, a number. We cannot know her
   *  choices, so no holiday may render. NOT the same as `absent`: absent is a
   *  known state (she has chosen nothing yet), this is an unknown one. */
  | { kind: 'unusable' };

const NO_MEMBERS: readonly string[] = Object.freeze([]);

/**
 * Read a raw `hiddenHolidays` field. Never throws, for any input.
 *
 * Silent about `absent` and loud about `unusable`/`droppedMembers`, the same
 * absence-is-silent policy `holidayEvents.validGuidePreview` established: the
 * absent case is the normal state for most users and warning on it would log
 * on nearly every page load, while a present-but-wrong value is a real data
 * problem worth surfacing. Nothing logged here ever includes the VALUE —
 * THE-746's ground rule is that holiday choices never leave our database, and
 * a browser console counts.
 */
export function readHiddenHolidays(raw: unknown): HiddenHolidaysRead {
  if (raw === undefined || raw === null) return { kind: 'absent', hidden: NO_MEMBERS };
  if (!Array.isArray(raw)) {
    // eslint-disable-next-line no-console
    console.warn('[holidayPrefs] hiddenHolidays is not an array; hiding all holidays');
    return { kind: 'unusable' };
  }
  const hidden = raw.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
  const droppedMembers = hidden.length !== raw.length;
  if (droppedMembers) {
    // eslint-disable-next-line no-console
    console.warn('[holidayPrefs] hiddenHolidays had unusable members; they were ignored');
  }
  return { kind: 'list', hidden, droppedMembers };
}

/** What a surface knows about the viewer's saved holiday setting. */
export interface HolidayPrefsView {
  /**
   * Has the saved list actually arrived? `useHolidayPrefs().ready`. Pass it
   * through; do not substitute `hiddenHolidays.length > 0` or a signed-in
   * check (see the module header — neither can see the pre-snapshot window).
   */
  ready: boolean;
  /** The raw `theaWebUser.hiddenHolidays`, as `useHolidayPrefs()` returns it. */
  hiddenHolidays?: unknown;
}

/**
 * The whole rule, as one predicate factory.
 *
 * Returns a predicate over a holiday's `eventType` — `false` for everything
 * when the setting is not knowable, otherwise `true` for everything she has
 * not hidden. Built once per call so the `Set` is not rebuilt per item.
 */
export function holidayVisibility(prefs: HolidayPrefsView): (eventType: string) => boolean {
  if (!prefs.ready) return () => false;
  const read = readHiddenHolidays(prefs.hiddenHolidays);
  if (read.kind === 'unusable') return () => false;
  if (read.hidden.length === 0) return () => true; // absent or empty: all on
  const hidden = new Set(read.hidden);
  return (eventType) => !hidden.has(eventType);
}

/**
 * Drop the holidays the viewer has hidden.
 *
 * Generic over anything carrying an `eventType` so both surfaces' shapes fit
 * without an adapter: `HolidayEntry` and dated `HolidayEntry` on the calendar
 * side, `ComingUpHolidayEntry.holiday` on the strip side.
 *
 * ONLY EVER CALL THIS ON HOLIDAYS. Person events (birthdays, anniversaries)
 * also carry an `eventType`, and they are not hers to hide — passing them
 * through here would make a `hiddenHolidays` entry that happens to collide
 * with `birthday` silently drop her sister.
 */
export function filterHiddenHolidays<T extends { eventType: string }>(
  holidays: readonly T[],
  prefs: HolidayPrefsView,
): T[] {
  const visible = holidayVisibility(prefs);
  return holidays.filter((h) => visible(h.eventType));
}
